import { Col, Divider, Pagination, Row, Spin, Table, Typography } from 'antd';
import { useQuery } from 'react-query';

import { ResultSummary } from '../../components/display';
import { ColumnModal } from '../../components/display/ColumnSettingModal/ColumnModal';
import {
  Button,
  FilterButton,
  Flex,
  Search,
} from '../../components/general';
import { ChangeView } from "../../components/general/Icons/Icons";
import { PageSizeOptions } from "../../constants/sorters.constants";
import { UseQueryTypes } from '../../constants/useQuery.constants';
import { useDrawer } from '../../contexts/drawer.context';
import { AppFormsEnum } from '../../forms';
import { useTable } from '../../hooks/table/useTable';
import { useColumnModalAdapter } from '../../hooks/useColumnModal.adapter';
import { useFilter } from '../../hooks/useFilter.hooks';
import { useSearchHandler } from '../../hooks/useSearchHandler.hooks';
import { defaultEmailColumns } from '../../schemas/email/email.schema';
import { filterEmails } from '../../services/mail.service';
import { SortDirectionEnum } from "../../types/general.types";
import { IMail, MailFilterOptions } from '../../types/mail.types';

const { Title } = Typography;

const defaultEmailFilterOptions: Partial<MailFilterOptions> = {
  sortBy: 'created_at',
  sortDirection: SortDirectionEnum.DESC,
  pageSize: PageSizeOptions.defaultPageSize,
};

export default function EmailHistory() {
  const { openDrawer } = useDrawer();
  const { handleSearch, term } = useSearchHandler();
  const { filterOptions, sorterOptions, resetOptions } = useFilter(
    defaultEmailFilterOptions,
  );

  const emailTable = useTable<IMail>(defaultEmailColumns);
  const modalAdapter = useColumnModalAdapter(
    emailTable.getTableColumnSettings(),
  );

  const emailsQuery = useQuery(
    [UseQueryTypes.FILTER_EMAILS, sorterOptions, filterOptions],
    () => filterEmails(filterOptions, sorterOptions),
  );

  const mailsTotalSize = !emailsQuery.isLoading
    ? emailsQuery?.data?.totalElements ?? 0
    : 0;

  const handleOpenFilterMailForm = () =>
    openDrawer(AppFormsEnum.FilterMailForm, 'Filter Email');

  const handleReset = () => {
    resetOptions();
  };

  const isScreenLoading = emailsQuery.isLoading;

  return (
    <>
      <Title level={2}>Mailing History</Title>

      <Spin spinning={isScreenLoading}>
        <Row gutter={16}>
          <Col span={22}>
            <Row justify="start" gutter={8}>
              <Col span={13}>
                <Row>
                  <Row>
                    <Search
                      style={{ marginRight: 8 }}
                      placeholder="Search"
                      defaultValue={term}
                      onChange={handleSearch}
                    />
                    <Flex alignItems="center" style={{ gap: '0 10px' }}>
                      <FilterButton onClick={handleOpenFilterMailForm}/>
                      <Button onClick={handleReset}>Reset Filters</Button>
                    </Flex>
                  </Row>
                </Row>
              </Col>

            </Row>
            <Divider style={{ margin: '16px 0px' }}/>
          </Col>

          <Col span={22}>
            <Row align="middle" justify="space-between">
              <ResultSummary text={`${mailsTotalSize} result found`}/>
            </Row>
            <Row justify="end">
              <ChangeView onClick={modalAdapter.openSettings}/>
            </Row>
          </Col>

          <Col span={24}>
            <Table<IMail>
              {...emailTable.getTableProps()}
              dataSource={emailsQuery?.data?.emails}
              rowKey={(email) => email.id}
            />
            <Pagination
              {...emailTable.getPaginationProps()}
              total={emailsQuery?.data?.totalElements}
              style={{ marginTop: 20 }}
            />
            <ColumnModal {...modalAdapter.getColumnModalProps()} />
          </Col>
        </Row>
      </Spin>
    </>
  );
}
